import React, {useEffect, useState} from 'react';
import GraphicalEnvelope from 'components/instruments/envelope/GraphicalEnvelope.js';
import {connect} from 'react-redux';
import Colors from 'components/Colors.js';
import Sample from 'repos/Sample.js';
import {
    setShowTrackEffects,
    setSampleDragging
} from 'actions/index.js';
import DocLink from './DocLink.js';
import SampledInstrumentComponent from 'components/instruments/sampledInstrument/SampledInstrumentComponent.js';
import {getSoundsWithTag} from 'search/getSounds.js';

const colors = new Colors();

const SampledInstrumentDocumentation = (props) => {
    let [track, setTrack] = useState(undefined);
    let [samples, setSamples] = useState([]);

    useEffect(() => {
        props.sequencerEngine.newSubSequence();
        props.sequencerEngine.switchToSubSequence(
            props.sequencerEngine.subSequences[props.sequencerEngine.subSequences.length - 1].id);
        let track = props.sequencerEngine.addSampledInstrumentTrack();
        setTrack(track);
        props.sequencerEngine.store.dispatch(
            setShowTrackEffects(0, true));
        return () => {
            props.sequencerEngine.deleteSubSequence();
            props.sequencerEngine.store.dispatch(
                setShowTrackEffects(0, false));
        };
    }, [setTrack]);

    useEffect(() => {
        getSoundsWithTag("kick").then(
            async (ids) => {
                let samples = [];
                for (let i=0; i < 8; i++) {
                    let sample = new Sample(ids[i]);
                    await sample.load();
                    samples.push(sample);
                }
                setSamples(samples);
            });
    }, [setSamples]);

    console.log('samples = ', samples);

    return (
        <div className="sampled-instrument-documentation">
          <p>
            The <b>Sampled Instrument</b> gives you more control over sample properties
            than the default sampler. To create a <b>Sampled Instrument</b>, go to the
            <b>Instruments tab</b> in the expanded sidebar and click on <b>Sampled Instrument</b>. The
            added track will be a <b>Sampled Instrument</b>, and will display the following
            in it's effects-view.
          </p>
          {track ? 
           <SampledInstrumentComponent
             instrument={track.instrument}
             track={track}
             sequencerEngine={props.sequencerEngine}/> : ''}
          <div className="two-column">
            <div className="text-column">
              <p className="sub-header">
                Adding Sounds
              </p>
              <p>
                Drag sounds into the box that says <b>Drop Sounds Here</b>. Once added to 
                the <b>Sampled Instrument</b>, you’ll see the waveform for that sound
                in the <b>Sampled instrument</b>.
              </p>
              <p>
                The colored thumbnails in each track, along with any sound in the sidebars, can be dragged in this manner.
              </p>
            </div>
            <div className="text-align">
              <div className="samples-to-drag">
                {samples.map(
                    x =>
                        <div
                          draggable={true}
                          onDragStart={() => {
                              console.log('setSampleDragging = ', x);
                              props.setSampleDragging(x);
                          }}
                          onDragOver={(e) => e.preventDefault()}
                          style={{backgroundColor: colors.getColorForHash(x.ipfsHash)}}
                          className="sample-option"></div>
                )}
                <span>
                  Drag these into the sampler
                </span>
              </div>
            </div>
          </div>
          <div className="two-column">
            <div className="text-column">
              <p className="sub-header">
                Looping Sounds
              </p>
              <p>
                Clicking the <b>Loop</b> checkbox in the Sampled Instrument, puts the instrument in <b>Loop Mode</b>, meaning the sample will repeat once it gets to the end. The track will loop based on the <b>Start</b> and <b>Length</b> parameters. 
              </p>
              <p>
                The <b>Start</b> and <b>Length</b> parameters are visualized in the waveform as red lines, indicating the start and end points. You can drag these lines to alter their values.
              </p>
              <p>
                Note: The <b>Start</b> parameter determines the start position of the sample, even when not in <b>Loop Mode</b>.
              </p>
            </div>
            <div className="text-column">
              <p className="sub-header">
                Monophonic Mode
              </p>
              <p>
                Clicking the <b>Mono</b> checkbox in the Sampled Instrument, puts the instrument in <b>Monophonic Mode</b>, meaning the sample will only play one note at once
              </p>
              <p>
                This is useful for bass where playing multiple notes at once is not desired.
              </p>
              <p className="sub-header">
                Glide
              </p>
              <p>
                The <b>Glide</b> parameter controls how long (in milliseconds) the sampler will spend sliding between notes in succession. 
              </p>
            </div>
          </div>
          <div className="two-column">
            <div className="text-column">
              <p className="sub-header">
                ADSR
              </p>
              <p>
                Various aspects of the sample's loudness can be controlled via <b>Attack</b>, <b>Decay</b>, <b>Sustain</b>,
                and <b>Release</b> (abbreviated ADSR). All these parameters, except sustain, use milliseconds as their unit.
              </p>
              <div><b>Attack</b>: How long to fade in.</div>
              <div><b>Decay</b>: How long after the attack it should reduce volume, until the sustain stage.</div>
              <div><b>Sustain</b>: How loud the sample should sustain-- after the decay stage-- until it is released.</div>
              <div><b>Release</b>: How long the sample should fade out once you release the note.</div>
            </div>
            <div className="text-column">
              <div className="adsr-demo">
                {track ? 
                 <GraphicalEnvelope
                   name="Amp"
                   backgroundColor="#545d6e"
                   fullText={true}
                   width={230}
                   fillColor={colors.brighten("#ff5100", 0.7)}
                   lineColor={"#ff5100"}
                   hasRelease={true}
                   hasLevel={false}
                 envelope={track.instrument}
                 /> : ''}
              </div>
              <p>
                <b>ADSR</b> is controlled with the above component. The first point (from the left), controls the
                <b>attack</b>. The second point controls the <b>decay</b>. The third point controls the <b>release</b>.
              </p>
              <p>
                The second and third points also control <b>sustain</b>, based on their vertical position.
              </p>
            </div>
          </div>
          <div className="two-column">
            <div className="text-column">
              <p className="sub-header">
                Transpose
              </p>
              <p>
                The <b>transpose</b> parameter controls the default transpose of the
                currently selected sample, based on the note triggered. In other
                words, it will pitch up or down the sample based on the transpose
                (whose units are in semitones--- i.e. notes).
              </p>
              <p>
                For example, <b>transpose</b> of 2 will pitch up every trigger by 2 semitones. 
              </p>
            </div>
            <div className="text-column">
              <p className="sub-header">
                Detune
              </p>
              <p>
                The <b>detune</b> parameter controls the detuning of the sample.
                The unit here is cents so +100 <b>cents</b> means it pitches up the sample by 1 semitone (or note).
              </p>
              <p>
                The <b>detune</b> parameter can be controlled by modulators, meaning you can wobble the
                pitch by and LFO or Envelope.
              </p>
            </div>
          </div>
          <p className="sub-header">
            Round Robin Sampling
          </p>
          <p>
            You can drag multiple samples into one key. 
          </p>
          <p>
            By default, these samples get triggered in a <b>round robin</b> fashion,
            meaning the samples get cycled through.
          </p>
          <p>
            In other words, on the first trigger, it will play the first sample.
            The second trigger will play the 2nd sample. The third trigger will
            play the 3rd sample. And so on.
          </p>
          <p>
            For example you can choose 3 different hi-hats and cycle through
            them by using <b>round robin</b>. The number of samples you choose to
            round robin with effects the groove, so play around with this.
          </p>
          <p className="sub-header">
            Laying Out Samples Onto Different Keys
          </p>
          <p>
            When trying to emulate an actual instrument, you may want to
            have different notes on the keyboard trigger different samples.
            This is possible by clicking the mini piano icon, which brings up the <b>Sampler Keyboard</b>.
          </p>
          <p>
            Drag samples onto different keys on the <b>Sampler Keyboard</b> to set them to that key. 
          </p>
          <p>
            Each key shares the sampler wide parameters <b>start</b>, <b>length</b>, <b>detune</b>,
            and <b>glide</b>. However, transpose applies to just the selected key.
          </p>
          <p>
            You can add multiple samples to a specific key. By default, the
            multiple samples will be cycled in the <b>round robin</b> manner.
          </p>
          <div className="two-column">
            <div className="text-column">
              <p className="sub-header">
                Browsing Sounds
              </p>
              <p>
                The arrow buttons, to the right of the sample’s name (at the top bar),
                allow you to browse between samples of that type. 
              </p>
              <p>
                Similarly, you can add new sounds by clicking the + button. When
                clicked, a search bar will apear where you can type in a sample tag. As you type, an auto-completed list of tags will appear below. 
              </p>
              <p>
                When you click “search” it will add a random sound of that type to the sampler.
              </p>
              <p>
                You can do complex queries, by typing “+” between tag names. For instance,
                the search query 808+kick will add a sound with tags 808 and kick to the sampler.
              </p>
            </div>
            <div className="text-column">
            </div>
          </div>
          <div className="two-column">
            <div className="text-column">
              <p className="sub-header">
                Parameter Modulation
              </p>
              <p>
                You can modulate each parameter using an interface similar to the effect rack. To modulate a paraemter by some modulator,  drag a connection from a modulator to the corresponding parameter.
              </p>
            </div>
            <div className="text-column">
            </div>
          </div>
        </div>
    );
};

const mapDispatchToProps = (dispatch) => {
  return {
      setSampleDragging: (x) => dispatch(setSampleDragging(x))
  };
};

export default connect(null, mapDispatchToProps)(SampledInstrumentDocumentation);

 
